import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { Btn } from '../primitives/Btn';
import { BtnOutline } from '../primitives/BtnOutline';
import { SystemArchitectureSVG } from '../primitives/SystemArchitectureSVG';
import { useIsMobile } from '../../hooks/useIsMobile';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import {
  ACCENT,
  BORDER,
  EASE,
  FONT_DISPLAY,
  FONT_MONO,
  MUTED,
  SURFACE2,
  SURFACE_ELEVATED,
  SURFACE_LIGHT,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
} from '../../tokens';

const FEED_MSGS: readonly string[] = [
  'Competitor scan complete, 14 content gaps found...',
  'Objection logged from Tuesday sales call...',
  'Drafting LinkedIn post from Objection Bank...',
  'Carousel queued for Wednesday, awaiting your approval...',
];

/** Normalised cursor offset from the section centre, each axis in [-1, 1]. */
interface MouseOffset {
  x: number;
  y: number;
}

interface HeroProps {
  ctaHref: string;
  onCta: (event: ReactMouseEvent<HTMLAnchorElement>) => void;
}

export function Hero({ ctaHref, onCta }: HeroProps) {
  const isMobile = useIsMobile();
  const reduced = usePrefersReducedMotion();

  const [text, setText] = useState('');
  const [msgIndex, setMsgIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const [mouse, setMouse] = useState<MouseOffset>({ x: 0, y: 0 });

  const sectionRef = useRef<HTMLElement>(null);

  // Scroll position, for the floating badge parallax.
  useEffect(() => {
    const handle = (): void => setScrollY(window.scrollY);
    window.addEventListener('scroll', handle, { passive: true });
    return () => window.removeEventListener('scroll', handle);
  }, []);

  // Cursor tracking for the card tilt, coalesced to one update per frame.
  useEffect(() => {
    if (reduced || isMobile) return;

    let raf = 0;
    let pending: MouseEvent | null = null;

    const handle = (event: MouseEvent): void => {
      pending = event;
      if (raf) return;

      raf = requestAnimationFrame(() => {
        raf = 0;
        const rect = sectionRef.current?.getBoundingClientRect();
        if (!rect || !pending) return;

        setMouse({
          x: ((pending.clientX - rect.left) / rect.width - 0.5) * 2,
          y: ((pending.clientY - rect.top) / rect.height - 0.5) * 2,
        });
        pending = null;
      });
    };

    window.addEventListener('mousemove', handle);
    return () => {
      window.removeEventListener('mousemove', handle);
      cancelAnimationFrame(raf);
    };
  }, [reduced, isMobile]);

  // Typewriter for the live-feed line.
  useEffect(() => {
    if (reduced) {
      setText(FEED_MSGS[0] ?? '');
      return;
    }

    const msg = FEED_MSGS[msgIndex] ?? '';

    if (charIndex <= msg.length) {
      const timer = setTimeout(
        () => {
          setText(msg.slice(0, charIndex));
          setCharIndex((c) => c + 1);
        },
        charIndex === 0 ? 600 : 38,
      );
      return () => clearTimeout(timer);
    }

    const timer = setTimeout(() => {
      setMsgIndex((m) => (m + 1) % FEED_MSGS.length);
      setCharIndex(0);
    }, 1800);
    return () => clearTimeout(timer);
  }, [charIndex, msgIndex, reduced]);

  const cardTiltX = reduced ? 0 : mouse.y * -6;
  const cardTiltY = reduced ? 0 : mouse.x * 8;
  const badgeDrift = reduced ? 0 : Math.min(scrollY * 0.12, 24);

  return (
    <section
      ref={sectionRef}
      style={{
        minHeight: '100dvh',
        background: 'rgba(8,8,8,0.88)',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Radial accent glow behind the content */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          right: '-5%',
          top: '20%',
          width: 700,
          height: 700,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,107,0,0.07) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          width: '100%',
          padding: isMobile ? '120px 20px 60px' : '160px 64px 100px',
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? 40 : 64,
          alignItems: 'center',
        }}
      >
        {/* Left column: copy, entering on a staggered CSS animation */}
        <div>
          <div style={{ animation: `heroFadeUp 0.9s ${EASE} 0.1s both` }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                background: 'rgba(255,107,0,0.08)',
                border: '1px solid rgba(255,107,0,0.25)',
                color: ACCENT,
                fontSize: '0.7rem',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                padding: '8px 18px',
                borderRadius: 100,
                marginBottom: 28,
                fontFamily: FONT_MONO,
                fontWeight: 500,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: ACCENT,
                  display: 'inline-block',
                  animation: 'pulse-dot 2s ease-in-out infinite',
                }}
              />
              Live in 14 days, guaranteed
            </div>
          </div>

          <div style={{ animation: `heroFadeUp 0.9s ${EASE} 0.22s both` }}>
            <h1
              style={{
                fontFamily: FONT_DISPLAY,
                fontSize: 'clamp(2.6rem,5vw,4.8rem)',
                fontWeight: 400,
                lineHeight: 1.02,
                letterSpacing: '0.02em',
                textTransform: 'uppercase',
                margin: '0 0 8px',
                color: TEXT_PRIMARY,
              }}
            >
              Your competitors are answering the questions your customers are asking. You&rsquo;re not.
            </h1>
          </div>

          <div style={{ animation: `heroFadeUp 0.9s ${EASE} 0.34s both` }}>
            <h2
              style={{
                fontFamily: FONT_DISPLAY,
                fontSize: 'clamp(1.4rem,2.5vw,2.2rem)',
                fontWeight: 400,
                lineHeight: 1.05,
                letterSpacing: '0.02em',
                textTransform: 'uppercase',
                margin: '0 0 20px',
                color: ACCENT,
              }}
            >
              They are not better at this. They are just more consistent.
            </h2>
          </div>

          <div style={{ animation: `heroFadeUp 0.9s ${EASE} 0.46s both` }}>
            <p
              style={{
                color: TEXT_SECONDARY,
                fontSize: 'clamp(1rem,1.2vw,1.1rem)',
                lineHeight: 1.75,
                maxWidth: 480,
                marginBottom: 32,
              }}
            >
              Content OS runs your content, your lead response, and your reviews as one system,
              grounded in your own sales calls and your competitors&rsquo; gaps, so what goes out
              sounds like you and answers what people are actually asking. You approve it. I build,
              write, and publish it.{' '}
              <strong style={{ color: TEXT_PRIMARY }}>About two hours a week from you.</strong>
            </p>
          </div>

          <div
            style={{
              animation: `heroFadeUp 0.9s ${EASE} 0.58s both`,
              display: 'flex',
              gap: 16,
              flexWrap: 'wrap',
            }}
          >
            <Btn href={ctaHref} onClick={onCta} style={{ fontSize: '0.95rem', padding: '14px 28px' }}>
              Get My Free Competitor Scan →
            </Btn>
            <BtnOutline href="#solution">See What You Get</BtnOutline>
          </div>
        </div>

        {/* Right column: architecture card with cursor-driven tilt */}
        {!isMobile && (
          <div style={{ animation: `heroFadeUp 0.9s ${EASE} 0.3s both`, position: 'relative', perspective: 1200 }}>
            <div
              style={{
                background: SURFACE_LIGHT,
                border: `1px solid ${BORDER}`,
                borderRadius: 24,
                padding: '28px 28px 20px',
                boxShadow: '0 0 80px rgba(255,107,0,0.1), 0 24px 60px rgba(0,0,0,0.4)',
                transform: `rotateX(${cardTiltX}deg) rotateY(${cardTiltY}deg)`,
                transition: reduced ? 'none' : `transform 0.2s ${EASE}`,
                transformStyle: 'preserve-3d',
              }}
            >
              <div
                style={{
                  background: SURFACE_ELEVATED,
                  border: '1px solid rgba(255,107,0,0.2)',
                  borderRadius: 12,
                  padding: '14px 18px',
                  marginBottom: 16,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <div
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      background: '#22c55e',
                      animation: 'pulse-dot 2s ease-in-out infinite',
                    }}
                  />
                  <span style={{ fontFamily: FONT_MONO, fontSize: '0.6rem', color: MUTED, letterSpacing: '0.1em' }}>
                    LIVE FEED, CONTENT PIPELINE
                  </span>
                </div>
                <div style={{ fontFamily: FONT_MONO, fontSize: '0.78rem', color: TEXT_PRIMARY, lineHeight: 1.6 }}>
                  <span style={{ color: ACCENT, marginRight: 4 }}>▶</span>
                  {text}
                  <span
                    style={{
                      display: 'inline-block',
                      width: 2,
                      height: '0.8em',
                      background: ACCENT,
                      marginLeft: 2,
                      verticalAlign: 'middle',
                      animation: 'blink 1.1s step-end infinite',
                    }}
                  />
                </div>
              </div>

              <SystemArchitectureSVG />
            </div>

            {/* Floating RPE badge, drifting up as the page scrolls */}
            <div
              style={{
                position: 'absolute',
                bottom: -16,
                left: -16,
                background: SURFACE_LIGHT,
                border: `1px solid ${BORDER}`,
                borderRadius: 14,
                padding: '12px 18px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                transform: `translateY(${-badgeDrift}px)`,
                transition: 'transform 0.1s linear',
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: ACCENT,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={SURFACE2} strokeWidth="2.5" aria-hidden="true">
                  <path d="M23 6l-9.5 9.5-5-5L1 18" />
                  <path d="M17 6h6v6" />
                </svg>
              </div>
              <div>
                <div style={{ fontFamily: FONT_DISPLAY, fontSize: '1.2rem', color: TEXT_PRIMARY, lineHeight: 1 }}>
                  43 pieces a month
                </div>
                <div style={{ fontSize: '0.7rem', color: MUTED }}>Written, checked, published.</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
